import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, catchError, Observable, throwError} from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { ContentLanguageService } from './content-language.service';

@Injectable({
  providedIn: 'root'
})
export class PageService {
  private readonly STORAGE_KEY = 'programs';
  private programsSubject = new BehaviorSubject<any[]>(this.loadFromLocalStorage());
  public programs$ = this.programsSubject.asObservable();
  private programsCache = new Map<string, Observable<any>>();

  constructor(
    private httpClient: HttpClient,
    private contentLanguageService: ContentLanguageService
  ) { }

  getChapters() {
    const url = environment.apiUrl + '/questionaries/chapters'

    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  getChaptersByQuestionary(questionaryId: string) {
    const url = environment.apiUrl + '/questionaries/chapters/' + questionaryId

    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  setPrograms(chapters: any[]): void {
    this.programsSubject.next(chapters);
    this.saveToLocalStorage(chapters);
  }

  getCurrentChapters(): any[] {
    return this.programsSubject.getValue();
  }

  private loadFromLocalStorage(): any[] {
    const storedPrograms = localStorage.getItem(this.STORAGE_KEY);
    return storedPrograms ? JSON.parse(storedPrograms) : [];
  }

  private saveToLocalStorage(programs: any[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(programs));
  }

  getPages() {
    const url = environment.apiUrl + '/questionaries/'

    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  getPageById(id: string | number, programId?: string) {
    const lang = this.contentLanguageService.getContentLang();
    const queryParam = `?program_id=${programId}&lang=${lang}`;
    let url = environment.apiUrl + '/questionaries/branch/' + id;

    if (programId) {
      url += queryParam;
    }

    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  getProgress() {
    const url = environment.apiUrl + '/questionaries/user/progress'

    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )
  }

  getQuestionariesByInstitution(institutionId: string) {
    const url = environment.apiUrl + '/questionaries/organization/' + institutionId

    return this.httpClient.get(url).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    )

  }

  getPrograms(forceRefresh = false): Observable<any> {
    const lang = this.contentLanguageService.getContentLang();
    const cacheKey = `programs_${lang}`;

    if (forceRefresh) {
      this.programsCache.delete(cacheKey);
    }

    if (!this.programsCache.has(cacheKey)) {
      const url = environment.apiUrl + '/questionaries/student/programs?lang=' + lang;

      const request$ = this.httpClient.get(url).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError(error => {
          this.programsCache.delete(cacheKey);
          return throwError(() => error);
        })
      );
      this.programsCache.set(cacheKey, request$);
    }

    return this.programsCache.get(cacheKey)!;
  }

  clearProgramsCache(): void {
    this.programsCache.clear();
  }
}
